import { useEffect, useState } from "react";
import { listUpcomingPartnerPayments, money, type PartnerUpcomingItem } from "@/lib/partnerPayments";
import { PartnerPaymentReviewDialog } from "./PartnerPaymentReviewDialog";

/**
 * Partner Payments section — shows revenue-share payees (currently Grace
 * Community Church) whose payment is due or approaching. Live-calculated,
 * NEVER auto-marked paid. Explicitly separate from instructor payroll.
 */
export function PartnerPaymentsSection() {
  const [items, setItems] = useState<PartnerUpcomingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState<PartnerUpcomingItem | null>(null);

  async function refresh() {
    try {
      const { items } = await listUpcomingPartnerPayments();
      setItems(items ?? []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void refresh(); }, []);

  // Only surface within ~30 days of due date OR overdue AND unpaid.
  const visible = items.filter((it) => !it.already_paid && it.period.days_until_due <= 30);
  if (loading || visible.length === 0) return null;

  return (
    <>
      {visible.map((item) => {
        const due = new Date(item.period.due_date + "T12:00:00");
        return (
          <section key={item.agreement_id} className="mt-8">
            <h2 className="text-lg font-serif text-foreground mb-3">
              Partner Payment Due — {item.payee.name} — {due.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
            </h2>
            <div className="rounded-lg border border-border/60 bg-card overflow-hidden">
              <div className="px-4 py-3 border-b border-border/50">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[15px] font-medium text-foreground">
                      {item.period.label} Period · {item.period.start} → {item.period.end}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {item.calculation.included_payment_ids.length} qualifying registrations · {item.calculation.percentage}% of net qualifying revenue
                    </p>
                  </div>
                  <span className="text-lg font-semibold text-foreground shrink-0">
                    {money(item.calculation.amount_due_cents)}
                  </span>
                </div>
              </div>
              <div className="px-4 py-3 text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
                <span>Gross: {money(item.calculation.gross_qualifying_revenue_cents)}</span>
                <span>Refunds: −{money(item.calculation.refund_adjustments_cents)}</span>
                <span>Net: {money(item.calculation.net_qualifying_revenue_cents)}</span>
                <span>Calculated {new Date(item.calculated_through).toLocaleString()}</span>
              </div>
              <div className="px-4 py-3 border-t border-border/50 flex gap-2">
                <button
                  onClick={() => setReviewing(item)}
                  className="text-sm font-medium text-primary hover:underline"
                >
                  Review Calculation
                </button>
              </div>
            </div>
          </section>
        );
      })}
      {reviewing && (
        <PartnerPaymentReviewDialog
          item={reviewing}
          onClose={() => setReviewing(null)}
          onFinalized={() => { setReviewing(null); void refresh(); }}
        />
      )}
    </>
  );
}
